import React, { Fragment, ReactElement, ReactNode, memo, useRef } from 'react';

import styled, { CSSObject } from 'styled-components';

import { BackgroundOverlay } from '@components/background-overlay';
import { IconProps } from '@components/icons';
import { IconApplicants } from '@components/icons/applicants';
import { IconEmail } from '@components/icons/email';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconSms } from '@components/icons/phone-sms';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

export const DESCRIPTION_LINE_COUNT_LIMIT = 3;

export type ActionCardHeaderProps = {
  children?: ReactNode;
  label: string;
  icon: ReactElement<IconProps>;
  labelColor?: Color;
  subLabel?: string;
};

export const ActionCardHeader = memo((props: ActionCardHeaderProps) => {
  const { children, label, icon, labelColor, subLabel } = props;
  const Container = subLabel ? Stack : Fragment;

  return (
    <StyledHeader>
      <Stack space={2} orientation="horizontal" align="center" style={{ overflow: 'hidden' }}>
        {icon}
        <Container>
          <Text font="semibold14" color={labelColor ?? 'textPrimary'} text={label} ellipsis />
          {subLabel && <Text font="regular12" color="textSecondary" text={subLabel} ellipsis />}
        </Container>
      </Stack>
      <Stack space={2} orientation="horizontal" align="center">
        {children}
      </Stack>
    </StyledHeader>
  );
});

type ActionCardContentProps = {
  children: ReactNode;
};

export const ActionCardContent = memo(
  (props: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>) => (
    <div {...props} />
  )
);

export type ActionCardDescriptionProps = {
  text?: ReactNode | string;
  isConfigured?: boolean;
  tooltip?: string;
};

export const ActionCardDescription = memo(
  ({ text, isConfigured = true, tooltip, ...rest }: ActionCardDescriptionProps) => {
    const { t } = useScopedTranslate(locale);
    const textRef = useRef<HTMLDivElement>(null);

    const description = text ? text : t('noDescription');
    const defaultDescription = isConfigured ? description : t('configureAction');
    return (
      <Description
        ref={textRef}
        font="regular14"
        color={text ? 'textSecondary' : 'textHelper'}
        text={defaultDescription}
        tooltip={tooltip}
        {...rest}
      />
    );
  }
);

export const descriptionStyle: CSSObject = {
  ...lineClamp('-webkit-box', DESCRIPTION_LINE_COUNT_LIMIT),
  minHeight: pxToRem(20),
};

const Description = styled(Text)(descriptionStyle);

const locale = {
  en: {
    noDescription: 'No description',
    configureAction: 'Configure action',
  },
};

type ActionCardBaseIconsProps = {
  warningIcon?: boolean;
  applicantIcon?: boolean;
  phoneIcon?: boolean;
  emailIcon?: boolean;
};

export const ActionCardBaseIcons = (props: ActionCardBaseIconsProps) => {
  const { warningIcon, applicantIcon, phoneIcon, emailIcon } = props;

  return (
    <Stack orientation="horizontal" space={2}>
      {warningIcon && <IconFormIndicatorWarning color="purple50" />}
      {applicantIcon && <IconApplicants color="purple50" />}
      {phoneIcon && <IconSms color="purple50" />}
      {emailIcon && <IconEmail color="purple50" />}
    </Stack>
  );
};

type ActionCardChild = ReactElement<ActionCardHeaderProps | ActionCardContentProps>;

export interface ActionCardProps {
  children: Array<ActionCardChild>;
  active?: boolean;
  backgroundColor?: Color;
  borderColor?: Color;
  skipHoverState?: boolean;
  isColorful?: boolean;
}

export const ActionCard = ({ children, ...rest }: ActionCardProps) => {
  let header = null;
  let content = null;

  React.Children.forEach(children, (child) => {
    if (!child) return null;

    if (child.type === ActionCardHeader) {
      header = child;
    } else if (child.type === ActionCardContent) {
      content = child;
    } else
      throw new Error(
        'this component only supports ActionCard.Header and ActionCard.Content as children'
      );
  });
  return (
    <StyledContainer {...rest}>
      <BackgroundOverlay />
      {header}
      {content}
    </StyledContainer>
  );
};
ActionCard.Header = ActionCardHeader;
ActionCard.Content = ActionCardContent;
ActionCard.Description = ActionCardDescription;

export const StyledContainer = styled.div<
  Pick<
    ActionCardProps,
    'active' | 'backgroundColor' | 'borderColor' | 'skipHoverState' | 'isColorful'
  >
>(({ active, backgroundColor, borderColor, skipHoverState, isColorful }) => {
  const activePaddingCompensation = active ? -1 : 0;
  const borderWidth = active ? 2 : 1;
  const defaultBorderColor = borderColor ? theme.color[borderColor] : theme.color.border20;

  return {
    position: 'relative',
    boxShadow: theme.shadow.focus,
    '--shadow-focus-width': pxToRem(8),
    '--shadow-focus-gap': 0,
    backgroundColor: backgroundColor
      ? theme.color[backgroundColor]
      : theme.color.backgroundLightDark,
    border: `${pxToRem(borderWidth)} solid ${defaultBorderColor}`,
    ...(!skipHoverState && {
      ':hover': {
        borderColor: active ? theme.color.elementBlue100 : defaultBorderColor,
        ...(!active && { '--shadow-focus-color': theme.color.uiTrans10 }),
      },
    }),
    ...(active &&
      (!isColorful
        ? {
            border: `${pxToRem(borderWidth)} solid ${theme.color.elementBlue100}`,
            '--shadow-focus-color': theme.color.blueTrans20,
          }
        : {
            ':before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: pxToRem(6),
              padding: pxToRem(2),
              background: theme.gradient.ai,
              WebkitMask: `linear-gradient(#fff 0 0) content-box,
                    linear-gradient(#fff 0 0)`,
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
            },
            ':after': {
              content: '""',
              position: 'absolute',
              top: pxToRem(-8),
              left: pxToRem(-8),
              right: pxToRem(-8),
              bottom: pxToRem(-8),
              borderRadius: pxToRem(16),
              background: theme.gradient.ai,
              opacity: 0.2,
              zIndex: -1,
            },
          })),
    borderRadius: pxToRem(8),
    paddingTop: pxToRem(8 + activePaddingCompensation),
    paddingRight: pxToRem(8 + activePaddingCompensation),
    paddingBottom: pxToRem(12 + activePaddingCompensation),
    paddingLeft: pxToRem(16 + activePaddingCompensation),
    width: '320px',
    transition: transitionStandard('border-color', 'box-shadow'),
  };
});

const StyledHeader = styled.div(() => ({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: pxToRem(2),
  minHeight: pxToRem(40),
}));
